
/**
 * Utility functions for color card rendering
 */

// Function to apply gradient to color card
function applyGradientToCard(cardElement, gradientCSS) {
  if (!cardElement || !gradientCSS) return;
  
  // Remove any existing background properties
  cardElement.style.removeProperty('background-color');
  cardElement.style.removeProperty('background-image');
  
  // Apply gradient as background (not background-image)
  cardElement.style.background = gradientCSS;
  
  console.log('Applied gradient to card:', gradientCSS);
}

// Function to create gradient CSS from color array with distinct sections
function createGradientCSS(colors) {
  if (!colors || colors.length === 0) return '';
  
  if (colors.length === 1) {
    return colors[0];
  }
  
  // Create distinct color sections without smooth transitions
  const stops = [];
  
  colors.forEach((color, index) => {
    const startPercentage = (index / colors.length) * 100;
    const endPercentage = ((index + 1) / colors.length) * 100;
    
    // Add start point for this color
    stops.push(`${color} ${startPercentage}%`);
    // Add end point for this color (creates sharp transition)
    stops.push(`${color} ${endPercentage}%`);
  });
  
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

// Function to calculate optimal height for color card names
function calculateOptimalCardHeight(cardNames) {
  if (!cardNames || cardNames.length === 0) return 30;
  
  console.log('Calculating optimal height for card names:', cardNames);
  
  // Create a temporary invisible element to measure text height WITHOUT padding
  const testElement = document.createElement('div');
  testElement.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: 88px;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.2;
    box-sizing: border-box;
    background-color: #FFFFFF;
    color: #000000;
  `;
  
  document.body.appendChild(testElement);
  
  let needsTwoLines = false;
  
  // Test each card name
  cardNames.forEach(name => {
    testElement.textContent = name;
    const height = testElement.scrollHeight;
    console.log(`Text "${name}" pure height: ${height}px`);
    
    // Adjusted threshold for pure text height - if more than ~20px, it needs two lines
    // Single line text should be ~14-16px, two line text should be ~28-30px
    if (height > 20) {
      needsTwoLines = true;
      console.log(`Text "${name}" NEEDS TWO LINES (height: ${height}px > 20px threshold)`);
    } else {
      console.log(`Text "${name}" fits in one line (height: ${height}px <= 20px threshold)`);
    }
  });
  
  document.body.removeChild(testElement);
  
  const optimalHeight = needsTwoLines ? 45 : 30;
  console.log(`Final optimal height determined: ${optimalHeight}px (needsTwoLines: ${needsTwoLines})`);
  
  return optimalHeight;
}

// Function to calculate optimal height for image card names (same logic as color cards)
function calculateOptimalImageCardHeight(cardNames) {
  if (!cardNames || cardNames.length === 0) return 30;
  
  console.log('Calculating optimal height for image card names:', cardNames);
  
  // Create a temporary invisible element to measure text height WITHOUT padding
  const testElement = document.createElement('div');
  testElement.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: 88px;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.2;
    box-sizing: border-box;
    background-color: #FFFFFF;
    color: #000000;
  `;
  
  document.body.appendChild(testElement);
  
  let needsTwoLines = false;
  
  // Test each card name
  cardNames.forEach(name => {
    testElement.textContent = name;
    const height = testElement.scrollHeight;
    console.log(`Image card text "${name}" pure height: ${height}px`);
    
    // Same threshold logic as color cards
    if (height > 20) {
      needsTwoLines = true;
      console.log(`Image card text "${name}" NEEDS TWO LINES (height: ${height}px > 20px threshold)`);
    } else {
      console.log(`Image card text "${name}" fits in one line (height: ${height}px <= 20px threshold)`);
    }
  });
  
  document.body.removeChild(testElement);
  
  const optimalHeight = needsTwoLines ? 45 : 30;
  console.log(`Final optimal height for image cards determined: ${optimalHeight}px (needsTwoLines: ${needsTwoLines})`);
  
  return optimalHeight;
}

// Function to apply calculated height to all color card names with retry mechanism
function applyOptimalHeightToCards(height, retryCount = 0) {
  const maxRetries = 3;
  const cardNames = document.querySelectorAll('.letz-ai__color-card-name');
  
  if (cardNames.length === 0 && retryCount < maxRetries) {
    console.log(`No color card names found, retrying in 100ms (attempt ${retryCount + 1}/${maxRetries})`);
    setTimeout(() => applyOptimalHeightToCards(height, retryCount + 1), 100);
    return;
  }
  
  console.log(`Applying height ${height}px to ${cardNames.length} color card names...`);
  
  cardNames.forEach((cardName, index) => {
    const oldHeight = cardName.style.height || 'not set';
    // Use !important to override CSS specificity
    cardName.style.setProperty('height', `${height}px`, 'important');
    console.log(`Color card ${index}: changed height from "${oldHeight}" to "${height}px"`);
  });
  
  console.log(`Successfully applied height ${height}px to ${cardNames.length} color card names with !important`);
}

// Function to apply calculated height to all image card names with retry mechanism
function applyOptimalHeightToImageCards(height, retryCount = 0) {
  const maxRetries = 3;
  const cardNames = document.querySelectorAll('.letz-ai__image-card-name');
  
  if (cardNames.length === 0 && retryCount < maxRetries) {
    console.log(`No image card names found, retrying in 100ms (attempt ${retryCount + 1}/${maxRetries})`);
    setTimeout(() => applyOptimalHeightToImageCards(height, retryCount + 1), 100);
    return;
  }
  
  console.log(`Applying height ${height}px to ${cardNames.length} image card names...`);
  
  cardNames.forEach((cardName, index) => {
    const oldHeight = cardName.style.height || 'not set';
    // Use !important to override CSS specificity
    cardName.style.setProperty('height', `${height}px`, 'important');
    console.log(`Image card ${index}: changed height from "${oldHeight}" to "${height}px"`);
  });
  
  console.log(`Successfully applied height ${height}px to ${cardNames.length} image card names with !important`);
}

// Function to apply height with proper timing for color cards
function applyHeightWithDelay(height, delay = 50) {
  console.log(`Scheduling color card height application: ${height}px with ${delay}ms delay`);
  // Use requestAnimationFrame for better timing
  requestAnimationFrame(() => {
    setTimeout(() => {
      applyOptimalHeightToCards(height);
    }, delay);
  });
}

// Function to apply height with proper timing for image cards
function applyImageCardHeightWithDelay(height, delay = 50) {
  console.log(`Scheduling image card height application: ${height}px with ${delay}ms delay`);
  // Use requestAnimationFrame for better timing
  requestAnimationFrame(() => {
    setTimeout(() => {
      applyOptimalHeightToImageCards(height);
    }, delay);
  });
}

// Export functions for use in other scripts
window.ColorCardUtils = {
  applyGradientToCard,
  createGradientCSS,
  calculateOptimalCardHeight,
  calculateOptimalImageCardHeight,
  applyOptimalHeightToCards,
  applyOptimalHeightToImageCards,
  applyHeightWithDelay,
  applyImageCardHeightWithDelay
};

console.log('ColorCardUtils loaded and available with image card support');
